'use client'

import { useEffect, useState } from 'react'

import { Button } from '@src/components/Button/Button'
import { Container } from '@src/components/Container/Container'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import type {
  TopNavV6SubNavItem,
  TopNavV6ThirdLevelMenu,
  TopNavV6ThirdLevelMenuItem,
} from '@src/components/TopNavV6/TopNavV6Props'
import { TopNavV6Tile } from '@src/components/TopNavV6/TopNavV6Tile'
import { cn } from '@src/utils/tailwind'

interface TopNavV6DesktopThirdLevelMenuProps {
  menu: TopNavV6ThirdLevelMenu
  subNavItem: TopNavV6SubNavItem
  isVisible: boolean
  onMouseLeave?: () => void
}

const MenuItem = ({ item }: { item: TopNavV6ThirdLevelMenuItem }) => {
  const isShopAll = item.isShopAll

  const content = (
    <div className="flex items-center gap-1">
      <span
        className={cn('font-sans text-body-sm leading-5 transition-colors group-hover:underline', {
          'text-bodi-gray-butters group-hover:text-bodi-white': !isShopAll,
          'text-bodi-green-bright-500': isShopAll,
          'group-hover:decoration-bodi-white': !isShopAll,
          'group-hover:decoration-bodi-green-bright-500': isShopAll,
        })}
      >
        {item.title}
      </span>
      {item.badge && (
        <div className="flex items-center gap-1 text-body-sm leading-5">
          <span className="font-sans font-normal text-bodi-gray-butters">-</span>
          <span className="text-center font-sans font-medium text-bodi-green-bright-500">
            {item.badge}
          </span>
        </div>
      )}
    </div>
  )

  if (item.href) {
    return (
      <LocalizedLink
        href={item.href}
        onClick={item.onClick}
        className="group flex items-center rounded-md transition-colors"
      >
        {content}
      </LocalizedLink>
    )
  }

  return (
    <div
      className="group flex items-center rounded-md transition-colors"
      onClick={item.onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          item.onClick?.()
        }
      }}
      role="button"
      tabIndex={0}
    >
      {content}
    </div>
  )
}

export const TopNavV6DesktopThirdLevelMenu = ({
  menu,
  subNavItem,
  isVisible,
  onMouseLeave,
}: TopNavV6DesktopThirdLevelMenuProps) => {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure the component is rendered in hidden state first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldShow(true)
        })
      })
    } else {
      setShouldShow(false)
    }
  }, [isVisible])

  const violators = (menu.violators ?? []).filter((violator) => violator.isVisible !== false)
  const violatorGridSize = menu.violatorGridSize ?? 1
  // If violators array is empty or violatorGridSize is 0, use 4 columns for content
  const effectiveViolatorGridSize =
    violators.length === 0 || violatorGridSize === 0 ? 0 : violatorGridSize
  const contentGridCols = 4 - effectiveViolatorGridSize

  return (
    <div
      onMouseLeave={onMouseLeave}
      className={cn(
        'absolute top-full left-0 z-30 w-full border-b border-bodi-gray-dark bg-bodi-black-text py-8 transition-opacity duration-300 ease-in-out',
        {
          'opacity-0 invisible pointer-events-none': !shouldShow,
          'opacity-100 visible': shouldShow,
        }
      )}
      style={{
        backgroundImage:
          'linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), linear-gradient(90deg, rgba(22, 24, 25, 1) 0%, rgba(22, 24, 25, 1) 100%)',
      }}
    >
      <Container>
        <div className="grid grid-cols-4 gap-8">
          {/* Content Columns Grid */}
          {menu.columns.length > 0 && (
            <div
              className={cn('grid gap-8', {
                'col-span-1 grid-cols-1': contentGridCols === 1,
                'col-span-2 grid-cols-2': contentGridCols === 2,
                'col-span-3 grid-cols-3': contentGridCols === 3,
                'col-span-4 grid-cols-4': contentGridCols === 4,
              })}
            >
              {menu.columns.map((column, columnIndex) => (
                <div key={`column-${columnIndex}`} className="flex flex-col">
                  {column.title && (
                    <div className="mb-2">
                      <h3 className="font-sans text-body-base leading-6 font-medium text-white">
                        {column.title}
                      </h3>
                    </div>
                  )}
                  {column.items && column.items.length > 0 && (
                    <div className="flex flex-col gap-1">
                      {column.items.map((item, itemIndex) => (
                        <MenuItem key={`item-${columnIndex}-${itemIndex}`} item={item} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Violators Grid */}
          {violators.length > 0 && effectiveViolatorGridSize > 0 && (
            <div
              className={cn('grid gap-8', {
                'col-span-1 grid-cols-1': effectiveViolatorGridSize === 1,
                'col-span-2 grid-cols-2': effectiveViolatorGridSize === 2,
                'col-span-3 grid-cols-3': effectiveViolatorGridSize === 3,
                'col-span-4 grid-cols-4': effectiveViolatorGridSize === 4,
              })}
            >
              {violators.map((violator, i) => (
                <div key={`violator-${i}`} className="flex flex-col">
                  <TopNavV6Tile tile={violator} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA Button */}
        {subNavItem.ctaButton && (
          <div className="mt-8 flex justify-start">
            {subNavItem.ctaButton.href ? (
              <LocalizedLink
                href={subNavItem.ctaButton.href}
                onClick={subNavItem.ctaButton.onClick}
              >
                <Button size="medium" variant="primary" contentClassName="tracking-normal">
                  {subNavItem.ctaButton.title}
                </Button>
              </LocalizedLink>
            ) : (
              <Button
                size="medium"
                variant="primary"
                contentClassName="tracking-normal"
                onClick={subNavItem.ctaButton.onClick}
              >
                {subNavItem.ctaButton.title}
              </Button>
            )}
          </div>
        )}
      </Container>
    </div>
  )
}
